import { Fragment, memo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  id: string;
  title: string;
  itemtype: string;
}

interface BreadcrumbsProps {
  currentItem: Record<string, unknown> | undefined;
  location: { pathname: string };
}

const FILE_TYPE_LABELS: Record<string, string> = {
  video: 'Videos',
  audio: 'Audios',
  image: 'Images',
  doc: 'Documents',
  allfiles: 'All files',
};

export const Breadcrumbs = memo(function Breadcrumbs({ currentItem, location }: BreadcrumbsProps) {
  const isRootDir = !currentItem;

  return (
    <nav className="flex items-center gap-1 text-sm">
      <Link
        to="/in/folder/root"
        className={`flex items-center gap-1 rounded px-1.5 py-0.5 no-underline transition-colors hover:bg-secondary ${isRootDir ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
      >
        <Home className="h-3.5 w-3.5" />
        <span>Home</span>
      </Link>

      {currentItem && (
        <>
          <FolderPath breadcrumb={getBreadcrumb(currentItem)} />
          <ActiveElement currentItem={currentItem} location={location} />
        </>
      )}
    </nav>
  );
});

function getBreadcrumb(item: Record<string, unknown>): BreadcrumbItem[] {
  const bc = item.breadcrumb as BreadcrumbItem[] | undefined;
  return bc || [];
}

function getItemType(item: Record<string, unknown>): string {
  return (item.itemtype as { S?: string })?.S || (item.itemtype as string) || '';
}

function getItemTitle(item: Record<string, unknown>): string {
  const title = (item.title as { S?: string; N?: string })?.S
    || (item.title as { N?: string })?.N
    || (item.title as string)
    || (item.name as { S?: string })?.S
    || (item.name as string)
    || '';
  return truncate(title, 30);
}

function getItemId(item: Record<string, unknown>): string {
  return (item.id as { S?: string })?.S || (item.id as string) || '';
}

// ── Separator ───────────────────────────────────────────────────────

function Separator() {
  return <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
}

// ── Folder path ─────────────────────────────────────────────────────

function FolderPath({ breadcrumb }: { breadcrumb: BreadcrumbItem[] }) {
  if (breadcrumb.length === 0) return null;

  // Collapse middle items if path is long
  const items = breadcrumb.length > 3
    ? [breadcrumb[0], null, breadcrumb[breadcrumb.length - 1]]
    : breadcrumb;

  return (
    <>
      {items.map((item, i) => (
        <Fragment key={item?.id ?? `ellipsis-${i}`}>
          <Separator />
          {item ? (
            <Link
              to={`/in/${item.itemtype}/${item.id}`}
              className="rounded px-1.5 py-0.5 text-muted-foreground no-underline transition-colors hover:bg-secondary hover:text-accent-foreground"
            >
              {truncate(item.title, 20)}
            </Link>
          ) : (
            <span className="px-1 text-muted-foreground">...</span>
          )}
        </Fragment>
      ))}
    </>
  );
}

// ── Active element (current page) ───────────────────────────────────

function ActiveElement({
  currentItem,
  location,
}: {
  currentItem: Record<string, unknown>;
  location: { pathname: string };
}) {
  const itemType = getItemType(currentItem);
  const title = getItemTitle(currentItem);
  const id = getItemId(currentItem);
  const urlParts = location.pathname.split('/');
  const lastSegment = urlParts[urlParts.length - 1];

  // Resource inside a project — show the project file type link
  if (itemType === 'resource') {
    const breadcrumb = getBreadcrumb(currentItem);
    const parent = breadcrumb[breadcrumb.length - 1];
    if (parent?.itemtype === 'project') {
      const viewType = FILE_TYPE_LABELS[lastSegment] ? lastSegment : (currentItem.type as { S?: string })?.S || (currentItem.type as string);
      return (
        <>
          <Separator />
          <Link
            to={`/in/project/${parent.id}/${viewType}`}
            className="rounded px-1.5 py-0.5 text-muted-foreground no-underline transition-colors hover:bg-secondary"
          >
            {FILE_TYPE_LABELS[viewType] || viewType}
          </Link>
          <Separator />
          <span className="px-1.5 py-0.5 font-medium text-foreground">{title}</span>
        </>
      );
    }
  }

  // Folder, project, or account — show as active breadcrumb
  if (itemType !== 'resource') {
    const passiveLabel = getPassiveLabel(lastSegment, itemType);
    return (
      <>
        <Separator />
        {passiveLabel ? (
          // Project with file type view or account with section
          <>
            <Link
              to={`/in/${itemType}/${id}`}
              className="rounded px-1.5 py-0.5 text-muted-foreground no-underline transition-colors hover:bg-secondary"
            >
              {title}
            </Link>
            <Separator />
            <span className="px-1.5 py-0.5 font-medium text-foreground">{passiveLabel}</span>
          </>
        ) : (
          <span className="px-1.5 py-0.5 font-medium text-foreground">{title}</span>
        )}
      </>
    );
  }

  // Resource in folder view
  return (
    <>
      <Separator />
      <span className="px-1.5 py-0.5 font-medium text-foreground">{title}</span>
    </>
  );
}

function getPassiveLabel(lastSegment: string, itemType: string): string | null {
  if (itemType === 'project' && FILE_TYPE_LABELS[lastSegment]) {
    return FILE_TYPE_LABELS[lastSegment];
  }
  if (itemType === 'account' && (lastSegment === 'profile' || lastSegment === 'workspace' || lastSegment === 'billing' || lastSegment === 'support')) {
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  }
  return null;
}

function truncate(text: string, maxLen: number): string {
  if (!text || text.length <= maxLen) return text;
  return text.slice(0, maxLen - 3) + '...';
}
